/**
 * Divisions — the lines of business everything else sorts by.
 *
 * Its own settings page rather than a catalog tab (`docs/catalog-and-packages.md`
 * §6.2), because a division is not a thing a quote is built out of: it is how
 * the company is organised. Crews, labor rates, services, packages and the
 * margin card all sort by these, which is why it is the first thing to set up
 * and why it belongs beside branches rather than beside mulch.
 */
import { useState } from 'react'
import { LENGTH_STEPS, minutesWords, type MeResponse } from '@rowted/shared'
import { CatalogList } from '../components/catalog-list.js'
import { MarkTile } from '../components/landing-marks.js'
import type { ListRow } from '../lib/use-list.js'
import { useSettingsBar } from '../settings/settings-bar.js'

/*
 * The name alone. The description has a column of its own two along, and a
 * row that printed it twice made the list look like it held twice as much as
 * it does — `org-07-divisions.png` draws the name and the code, nothing else.
 */
const nameAnd = (row: ListRow) => ({ primary: String(row.name ?? '') })

/*
 * Board 12 C's Estimate visit: the same 15-minute steps New request's Length
 * offers, so what a division starts a visit at is always a length the sheet
 * can show. A select holds strings; the API takes the number back.
 */
const ESTIMATE_OPTIONS = LENGTH_STEPS.map((minutes) => ({
  value: String(minutes), label: minutesWords(minutes),
}))

/**
 * The division's own tile — the mark every job in it wears.
 *
 * It was a plain colour square, which told you the colour and nothing else;
 * the whole point of §4's table is that a settings list shows the same mark
 * the module's rows show, so picking an icon here and seeing it on the Jobs
 * board is one loop rather than two screens and a guess.
 */
const divisionMark = (row: ListRow) => (
  <MarkTile
    set="division"
    slug={typeof row.icon === 'string' ? row.icon : null}
    colour={typeof row.colour === 'string' ? row.colour : null}
    size={28}
    title={String(row.name ?? 'Division')}
  />
)

export function Divisions({ me }: { me: MeResponse }) {
  const [reloadKey, setReloadKey] = useState(0)
  const canEdit = me.permissions.company_settings === 'edit'
    || me.permissions.company_settings === 'full'

  // The shell owns the page bar (`design-remediation.md` §1.1).
  useSettingsBar({
    title: 'Divisions',
    sub: 'the lines of business a quote is split by',
  })

  return (
    <div className="p-4">
      <CatalogList
        area="catalog-admin/divisions"
        title="Divisions"
        singular="division"
        canEdit={canEdit}
        importOrgUnitId={me.scope.selected[0]}
        reloadKey={reloadKey}
        onChanged={() => setReloadKey((key) => key + 1)}
        primary={nameAnd}
        mark={{ render: divisionMark }}
        alsoFetch={['colour', 'icon']}
        cell={{ code: (row) => <code className="text-cell">{String(row.code ?? '')}</code> }}
        blank={{
          name: '', code: '', description: '', sortOrder: 0, active: true,
          estimateMinutes: '60',
          /* No icon and no colour until somebody picks: both fields are
             required, and a blank that pre-picked green would be a choice
             nobody made showing as a choice they did. */
          icon: '', colour: '',
        }}
        fields={[
          { name: 'name', label: 'Name', required: true },
          {
            name: 'code', label: 'Code', required: true,
            placeholder: 'INSTALL',
            hint: 'Letters, digits, dashes and underscores — capitals are fine. This is '
              + 'what gets written onto crews and jobs, so it stays put when you rename '
              + 'the division.',
          },
          {
            name: 'estimateMinutes', label: 'Estimate visit', kind: 'select',
            options: ESTIMATE_OPTIONS, required: true,
            hint: 'how long a salesperson’s site visit usually takes for this division — '
              + 'New request starts with it',
          },
          /*
           * Icon, colour, preview — in that order and together, because they
           * are one decision (board 4). The preview is not decoration: a glyph
           * that reads at 56 can be a smudge at 28, and 28 is the list row.
           */
          {
            name: 'icon', label: 'Icon', kind: 'icon', set: 'division', required: true,
            hint: 'one of the division set · the glyph on every job in this division',
          },
          {
            name: 'colour', label: 'Colour', kind: 'mark-colour', required: true,
            hint: 'the tint behind the icon · also the division’s colour on the schedule '
              + 'and the board',
          },
          { name: 'preview', label: '', kind: 'preview', set: 'division' },
          { name: 'description', label: 'Description', kind: 'textarea' },
          { name: 'sortOrder', label: 'Sort order', kind: 'number' },
          { name: 'active', label: 'Active', kind: 'switch' },
        ]}
        emptyTitle="No divisions yet"
        emptyBody="A division is what kind of work it is. Crews, services and labor all sort by these, so it is the first thing to set up."
      />
    </div>
  )
}
